import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Euro, Calendar, Clock, User, Users, Building2, Package, ClipboardList,
  Hash, Timer, Percent, Banknote, Warehouse, Server, ShoppingCart,
  TrendingUp, GripVertical, X, Plus,
} from "lucide-react";

// Tous les champs disponibles pour la section "En évidence" — data-driven
export const AVAILABLE_HIGHLIGHT_FIELDS = [
  { id: "montant_abonnement",         label: "Abonnement mensuel",  icon: Euro,        color: "blue",   type: "currency" },
  { id: "abonnement_gestion_interne", label: "Abo. Gestion Interne", icon: Banknote,    color: "green",  type: "currency" },
  { id: "abonnement_finance",         label: "Abo. Finance",        icon: TrendingUp,  color: "orange", type: "currency" },
  { id: "date_signature",             label: "Date de signature",   icon: Calendar,    color: "green",  type: "date" },
  { id: "date_installation",          label: "Date d'installation", icon: Calendar,    color: "cyan",   type: "date" },
  { id: "date_fin",                   label: "Fin de contrat",      icon: Clock,       color: "orange", type: "date" },
  { id: "vendeur",                    label: "Vendeur",             icon: User,        color: "purple", type: "text" },
  { id: "revendeur",                  label: "Revendeur",           icon: Users,       color: "pink",   type: "text" },
  { id: "societe",                    label: "Société",             icon: Building2,   color: "slate",  type: "text" },
  { id: "produit",                    label: "Produit",             icon: Package,     color: "indigo", type: "text" },
  { id: "type",                       label: "Type",                icon: ClipboardList, color: "teal", type: "text" },
  { id: "duree_mois",                 label: "Durée (mois)",        icon: Timer,       color: "amber",  type: "months" },
  { id: "numero_contrat",             label: "N° Contrat",          icon: Hash,        color: "slate",  type: "text" },
  { id: "leaser",                     label: "Leaser",              icon: Banknote,    color: "emerald", type: "text" },
  { id: "numero_financement",         label: "N° Financement",      icon: Hash,        color: "slate",  type: "text" },
  { id: "ca_leaser",                  label: "CA Leaser",           icon: TrendingUp,  color: "green",  type: "currency" },
  { id: "commission",                 label: "Commission",          icon: Percent,     color: "yellow", type: "currency" },
  { id: "frais_installation",         label: "Frais installation",  icon: Warehouse,   color: "gray",   type: "currency" },
  { id: "cout_installation",          label: "Coût installation",   icon: Warehouse,   color: "gray",   type: "currency" },
  { id: "vente_materiel",             label: "Vente matériel",      icon: ShoppingCart, color: "blue",  type: "currency" },
  { id: "location_serveur",           label: "Location serveur",    icon: Server,      color: "indigo", type: "currency" },
  { id: "rachat",                     label: "Rachat",              icon: Euro,        color: "red",    type: "currency" },
];

const COLOR_CLASSES = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-600" },
  green:   { bg: "bg-green-50",   text: "text-green-600" },
  orange:  { bg: "bg-orange-50",  text: "text-orange-600" },
  purple:  { bg: "bg-purple-50",  text: "text-purple-600" },
  cyan:    { bg: "bg-cyan-50",    text: "text-cyan-600" },
  pink:    { bg: "bg-pink-50",    text: "text-pink-600" },
  slate:   { bg: "bg-slate-100",  text: "text-slate-600" },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-600" },
  teal:    { bg: "bg-teal-50",    text: "text-teal-600" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-600" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600" },
  yellow:  { bg: "bg-yellow-50",  text: "text-yellow-600" },
  gray:    { bg: "bg-gray-100",   text: "text-gray-600" },
  red:     { bg: "bg-red-50",     text: "text-red-600" },
};

const DEFAULT_HIGHLIGHT_IDS = ["montant_abonnement", "date_signature", "date_fin", "vendeur"];

const STORAGE_KEY = "contract_highlight_fields";

export const loadHighlightConfig = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const ids = JSON.parse(saved);
      // Filtre les IDs qui n'existent plus (schéma mis à jour)
      return ids.filter(id => AVAILABLE_HIGHLIGHT_FIELDS.some(f => f.id === id));
    }
  } catch (e) { /* fallback */ }
  return DEFAULT_HIGHLIGHT_IDS;
};

const saveHighlightConfig = (ids) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch (e) { /* ignore */ }
};

const formatValue = (contract, field) => {
  const raw = contract[field.id];
  if (raw === undefined || raw === null || raw === "") return "-";
  if (field.type === "currency") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(raw || 0);
  }
  if (field.type === "date") {
    try {
      return new Date(raw).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
    } catch (e) { return String(raw); }
  }
  if (field.type === "months") return `${raw} mois`;
  return String(raw);
};

export default function ContractHighlightFields({ contract }) {
  const [highlightIds, setHighlightIds] = useState(() => loadHighlightConfig());
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const highlightFields = highlightIds
    .map(id => AVAILABLE_HIGHLIGHT_FIELDS.find(f => f.id === id))
    .filter(Boolean);
  const hiddenFields = AVAILABLE_HIGHLIGHT_FIELDS.filter(f => !highlightIds.includes(f.id));

  const updateIds = (newIds) => {
    setHighlightIds(newIds);
    saveHighlightConfig(newIds);
  };

  const removeField = (id) => updateIds(highlightIds.filter(x => x !== id));
  const addField = (id) => updateIds([...highlightIds, id]);

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== draggedId) setDragOverId(id);
  };
  const handleDragLeave = () => setDragOverId(null);
  const handleDragEnd = () => { setDraggedId(null); setDragOverId(null); };
  const handleDrop = (e, dropId) => {
    e.preventDefault();
    if (!draggedId || draggedId === dropId) { handleDragEnd(); return; }
    const dragIndex = highlightIds.indexOf(draggedId);
    const dropIndex = highlightIds.indexOf(dropId);
    const newIds = [...highlightIds];
    const [moved] = newIds.splice(dragIndex, 1);
    newIds.splice(dropIndex, 0, moved);
    updateIds(newIds);
    handleDragEnd();
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
      {highlightFields.map((field) => {
        const Icon = field.icon;
        const color = COLOR_CLASSES[field.color] || COLOR_CLASSES.slate;
        return (
          <div
            key={field.id}
            draggable
            onDragStart={(e) => handleDragStart(e, field.id)}
            onDragOver={(e) => handleDragOver(e, field.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, field.id)}
            onDragEnd={handleDragEnd}
            title="Glisser pour réorganiser"
            className={`relative select-none cursor-move rounded-xl border bg-white shadow-sm transition-all group
              ${draggedId === field.id ? "opacity-40" : ""}
              ${dragOverId === field.id ? "ring-2 ring-blue-400 border-blue-400" : ""}
            `}
            data-testid={`highlight-field-${field.id}`}
          >
            <div className="p-4 flex items-center gap-3">
              <GripVertical className="w-3 h-3 text-muted-foreground opacity-30 group-hover:opacity-70 transition-opacity flex-shrink-0" />
              <div className={`p-2 rounded-lg ${color.bg} flex-shrink-0`}>
                <Icon className={`w-5 h-5 ${color.text}`} />
              </div>
              <div className="min-w-0 flex-1 pointer-events-none">
                <p className="text-xs text-muted-foreground truncate">{field.label}</p>
                <p className="text-lg font-bold truncate" title={formatValue(contract, field)}>
                  {formatValue(contract, field)}
                </p>
              </div>
              {/* Bouton X pour retirer */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                onMouseDown={(e) => e.stopPropagation()}
                draggable={false}
                className="opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:text-red-600 transition-opacity flex-shrink-0"
                title="Retirer de la mise en évidence"
                data-testid={`remove-highlight-${field.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}

      {/* Bouton "+" pour ajouter un champ */}
      {hiddenFields.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-blue-400 hover:text-blue-600 transition min-h-[76px]"
              title="Ajouter un champ en évidence"
              data-testid="add-highlight-btn"
            >
              <Plus className="w-5 h-5" />
              <span className="text-xs font-medium">Ajouter</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-1" align="end">
            <p className="text-xs font-medium text-muted-foreground px-2 py-1.5 uppercase tracking-wider">
              Champs disponibles
            </p>
            <div className="max-h-72 overflow-y-auto">
              {hiddenFields.map((field) => {
                const Icon = field.icon;
                const color = COLOR_CLASSES[field.color] || COLOR_CLASSES.slate;
                return (
                  <button
                    key={field.id}
                    type="button"
                    onClick={() => addField(field.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-blue-50 rounded transition text-left"
                    data-testid={`show-highlight-${field.id}`}
                  >
                    <div className={`p-1 rounded ${color.bg} flex-shrink-0`}>
                      <Icon className={`w-3.5 h-3.5 ${color.text}`} />
                    </div>
                    <span className="flex-1 truncate">{field.label}</span>
                    <Plus className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
